import type { Job } from '@volt/shared';
import type { TranslationKey } from '../i18n/index.js';

export const JOB_POLL_BASE_MS = 4000;
export const JOB_POLL_MAX_MS = 30_000;

/** True while at least one job still needs the feed to be polled. */
export function hasActiveJobs(jobs: Job[]): boolean {
  return jobs.some((job) => job.status === 'pending' || job.status === 'processing');
}

/** Poll interval with exponential backoff after `errors` consecutive failures. */
export function pollDelayMs(errors: number, base = JOB_POLL_BASE_MS, max = JOB_POLL_MAX_MS): number {
  if (errors <= 0) return base;
  return Math.min(max, base * 2 ** Math.min(errors, 10));
}

/**
 * Merges a freshly polled first page into the list on screen. A job whose `status` and
 * `updatedAt` did not change keeps its previous object, so tiles, players and lightboxes
 * do not re-render (or restart playback) on every poll; the head order comes from the
 * server, jobs beyond the first page are kept in place.
 */
export function mergeJobsHead(prev: Job[], head: Job[], deletedIds: ReadonlySet<string>): Job[] {
  const previous = new Map(prev.map((job) => [job.id, job]));
  const headIds = new Set(head.map((job) => job.id));
  const freshHead = head
    .filter((job) => !deletedIds.has(job.id))
    .map((job) => {
      const before = previous.get(job.id);
      return before && before.status === job.status && before.updatedAt === job.updatedAt
        ? before
        : job;
    });
  const tail = prev.filter((job) => !headIds.has(job.id) && !deletedIds.has(job.id));
  const next = [...freshHead, ...tail];
  const unchanged =
    next.length === prev.length && next.every((job, index) => job === prev[index]);
  return unchanged ? prev : next;
}

/** `true` for an API error the session cannot recover from by polling again. */
export function isUnauthorizedError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401;
}

/**
 * Message for a failed job request. A 4xx validation error carries a specific reason
 * from the server (which option is wrong, which file is too large), so that is shown
 * instead of the generic text for the error code.
 */
export function describeJobError(
  error: unknown,
  t: (key: TranslationKey) => string,
): string {
  const { code, status, message } = (error ?? {}) as {
    code?: unknown;
    status?: unknown;
    message?: unknown;
  };
  const codeKey = typeof code === 'string' && code ? code : 'INTERNAL';
  const serverMessage = typeof message === 'string' ? message.trim() : '';
  const isClientError = typeof status === 'number' && status >= 400 && status < 500;
  if (isClientError && (codeKey === 'VALIDATION_ERROR' || codeKey === 'PAYLOAD_TOO_LARGE')) {
    if (serverMessage && serverMessage !== 'Invalid request') return serverMessage;
  }
  const key = `error.${codeKey}` as TranslationKey;
  const translated = t(key);
  return translated === key ? t('error.generic') : translated;
}
